import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Building2, 
  ExternalLink,
  Pencil,
  Trash2,
  Eye,
  Download
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const ClaimsPage = ({ user }) => {
  const [claims, setClaims] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  
  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    policy_number: "",
    insured_name: "",
    company_id: "",
    status: "pending_survey",
    description: "",
    lhs_link: ""
  });

  const canEdit = user?.role === "superuser" || user?.role === "administrator";

  useEffect(() => {
    fetchData();
  }, [searchQuery, statusFilter, companyFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (companyFilter !== "all") params.append("company_id", companyFilter);

      const [claimsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/claims?${params.toString()}`),
        axios.get(`${API}/companies`)
      ]);
      
      setClaims(claimsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.policy_number || !formData.insured_name || !formData.company_id) {
        toast.error("No. Polis, Nama Tertanggung, dan Perusahaan wajib diisi");
        return;
      }
      
      await axios.post(`${API}/claims`, formData);
      toast.success("Klaim berhasil dibuat");
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating claim:", error);
      toast.error(error.response?.data?.detail || "Gagal membuat klaim");
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/claims/${selectedClaim.claim_id}`, formData);
      toast.success("Klaim berhasil diupdate");
      setIsEditOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error updating claim:", error);
      toast.error(error.response?.data?.detail || "Gagal mengupdate klaim");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/claims/${selectedClaim.claim_id}`);
      toast.success("Klaim berhasil dihapus");
      setIsDeleteOpen(false);
      setSelectedClaim(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting claim:", error);
      toast.error(error.response?.data?.detail || "Gagal menghapus klaim");
    }
  };

  const openEditDialog = (claim) => {
    setSelectedClaim(claim);
    setFormData({
      policy_number: claim.policy_number,
      insured_name: claim.insured_name || "",
      company_id: claim.company_id,
      status: claim.status,
      description: claim.description || "",
      lhs_link: claim.lhs_link || ""
    });
    setIsEditOpen(true);
  };

  const openViewDialog = (claim) => {
    setSelectedClaim(claim);
    setIsViewOpen(true);
  };

  const resetForm = () => {
    setFormData({
      policy_number: "",
      insured_name: "",
      company_id: "",
      status: "pending_survey",
      description: "",
      lhs_link: ""
    });
    setSelectedClaim(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_survey: { label: "Pending Survey", class: "badge-pending" },
      proses_survey: { label: "Proses Survey", class: "badge-process" },
      klaim_ditolak: { label: "Ditolak", class: "badge-rejected" },
      klaim_dijamin: { label: "Dijamin", class: "badge-approved" }
    };
    const config = statusConfig[status] || { label: status, class: "badge-pending" };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const statusOptions = [
    { value: "pending_survey", label: "Pending Survey" },
    { value: "proses_survey", label: "Proses Survey" },
    { value: "klaim_ditolak", label: "Klaim Ditolak" },
    { value: "klaim_dijamin", label: "Klaim Dijamin" }
  ];

  return (
    <Layout user={user}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Daftar Klaim</h1>
            <p className="page-subtitle">Kelola semua klaim investigasi asuransi</p>
          </div>
          {canEdit && (
            <Button 
              className="btn-accent"
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }}
              data-testid="create-claim-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Buat Klaim Baru
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Cari no. polis, perusahaan, atau deskripsi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Company Filter - Only show for Admin/SuperUser */}
              {(user?.role === "superuser" || user?.role === "administrator") && (
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="w-full md:w-48" data-testid="company-filter">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter Perusahaan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Perusahaan</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Claims Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : claims.length > 0 ? (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No. Polis/Klaim</th>
                      <th>Nama Tertanggung</th>
                      <th>Perusahaan Asuransi</th>
                      <th>Status</th>
                      <th>Deskripsi</th>
                      <th>LHS</th>
                      <th>Tanggal</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((claim) => (
                      <tr 
                        key={claim.claim_id} 
                        data-testid={`claim-row-${claim.claim_id}`}
                        onClick={() => openViewDialog(claim)}
                        className="cursor-pointer"
                      >
                        <td className="font-medium">{claim.policy_number}</td>
                        <td>{claim.insured_name || "-"}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {claim.company_name}
                          </div>
                        </td>
                        <td>{getStatusBadge(claim.status)}</td>
                        <td className="max-w-xs truncate">{claim.description || "-"}</td>
                        <td>
                          {claim.lhs_link ? (
                            <a 
                              href={claim.lhs_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              data-testid={`lhs-link-${claim.claim_id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="text-slate-500">
                          {new Date(claim.created_at).toLocaleDateString('id-ID')}
                        </td>
                        <td>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openViewDialog(claim)}
                              data-testid={`view-claim-${claim.claim_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canEdit && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(claim)}
                                  data-testid={`edit-claim-${claim.claim_id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setSelectedClaim(claim);
                                    setIsDeleteOpen(true);
                                  }}
                                  data-testid={`delete-claim-${claim.claim_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state py-16">
                <FileText className="empty-state-icon" />
                <p className="empty-state-title">Tidak ada klaim ditemukan</p>
                <p className="empty-state-description">
                  {searchQuery || statusFilter !== "all" || companyFilter !== "all"
                    ? "Coba ubah filter pencarian Anda"
                    : "Belum ada data klaim yang tersedia"}
                </p>
                {canEdit && !searchQuery && statusFilter === "all" && companyFilter === "all" && (
                  <Button 
                    className="btn-accent"
                    onClick={() => {
                      resetForm();
                      setIsCreateOpen(true);
                    }}
                    data-testid="create-first-claim-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Buat Klaim Pertama
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Buat Klaim Baru</DialogTitle>
              <DialogDescription>
                Masukkan data klaim investigasi baru
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">No. Polis/Klaim *</Label>
                <Input
                  data-testid="input-policy-number"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  placeholder="Masukkan no. polis/klaim"
                />
              </div>
              <div>
                <Label className="form-label">Nama Tertanggung *</Label>
                <Input
                  data-testid="input-insured-name"
                  value={formData.insured_name}
                  onChange={(e) => setFormData({ ...formData, insured_name: e.target.value })}
                  placeholder="Masukkan nama tertanggung"
                />
              </div>
              <div>
                <Label className="form-label">Perusahaan Asuransi *</Label>
                <Select 
                  value={formData.company_id} 
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="select-company">
                    <SelectValue placeholder="Pilih perusahaan asuransi" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Status Klaim *</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Pilih status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Deskripsi Hasil Survey</Label>
                <Textarea
                  data-testid="input-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Masukkan deskripsi hasil survey...&#10;&#10;Contoh:&#10;- Lokasi kejadian&#10;- Kronologi&#10;- Temuan survey&#10;- Kesimpulan"
                  rows={6}
                  className="resize-none"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Gunakan Enter untuk membuat paragraf baru
                </p>
              </div>
              <div>
                <Label className="form-label">Link Download LHS</Label>
                <Input
                  data-testid="input-lhs-link"
                  value={formData.lhs_link}
                  onChange={(e) => setFormData({ ...formData, lhs_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleCreate} data-testid="submit-create-claim">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Klaim</DialogTitle>
              <DialogDescription>
                Perbarui data klaim investigasi
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">No. Polis/Klaim *</Label>
                <Input
                  data-testid="edit-policy-number"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  placeholder="Masukkan no. polis/klaim"
                />
              </div>
              <div>
                <Label className="form-label">Nama Tertanggung *</Label>
                <Input
                  data-testid="edit-insured-name"
                  value={formData.insured_name}
                  onChange={(e) => setFormData({ ...formData, insured_name: e.target.value })}
                  placeholder="Masukkan nama tertanggung"
                />
              </div>
              <div>
                <Label className="form-label">Perusahaan Asuransi *</Label>
                <Select 
                  value={formData.company_id} 
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="edit-select-company">
                    <SelectValue placeholder="Pilih perusahaan asuransi" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Status Klaim *</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="edit-select-status">
                    <SelectValue placeholder="Pilih status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Deskripsi Hasil Survey</Label>
                <Textarea
                  data-testid="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Masukkan deskripsi hasil survey...&#10;&#10;Contoh:&#10;- Lokasi kejadian&#10;- Kronologi&#10;- Temuan survey&#10;- Kesimpulan"
                  rows={6}
                  className="resize-none"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Gunakan Enter untuk membuat paragraf baru
                </p>
              </div>
              <div>
                <Label className="form-label">Link Download LHS</Label>
                <Input
                  data-testid="edit-lhs-link"
                  value={formData.lhs_link}
                  onChange={(e) => setFormData({ ...formData, lhs_link: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleEdit} data-testid="submit-edit-claim">
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Detail Klaim</DialogTitle>
              <DialogDescription>
                Informasi lengkap klaim investigasi
              </DialogDescription>
            </DialogHeader>
            {selectedClaim && (
              <div className="space-y-6 py-4">
                {/* Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">No. Polis/Klaim</Label>
                    <p className="text-base font-semibold text-slate-900">{selectedClaim.policy_number}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">Nama Tertanggung</Label>
                    <p className="text-base font-semibold text-slate-900">{selectedClaim.insured_name || "-"}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">Perusahaan Asuransi</Label>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <p className="text-base font-semibold text-slate-900">{selectedClaim.company_name}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">Status Klaim</Label>
                    <div className="mt-1">{getStatusBadge(selectedClaim.status)}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">Tanggal Dibuat</Label>
                    <p className="text-base font-semibold text-slate-900">
                      {new Date(selectedClaim.created_at).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-slate-500">Terakhir Diupdate</Label>
                    <p className="text-base font-semibold text-slate-900">
                      {selectedClaim.updated_at 
                        ? new Date(selectedClaim.updated_at).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-slate-200"></div>

                {/* Deskripsi Hasil Survey */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-500">Deskripsi Hasil Survey</Label>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 min-h-[120px]">
                    {selectedClaim.description ? (
                      <p className="text-slate-700 text-base leading-relaxed whitespace-pre-wrap break-words">
                        {selectedClaim.description}
                      </p>
                    ) : (
                      <p className="text-slate-400 italic">Tidak ada deskripsi hasil survey</p>
                    )}
                  </div>
                </div>

                {/* Laporan Hasil Survey (LHS) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-500">Laporan Hasil Survey (LHS)</Label>
                  {selectedClaim.lhs_link ? (
                    <a 
                      href={selectedClaim.lhs_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-3 rounded-lg border border-blue-200 transition-colors"
                      data-testid="view-lhs-link"
                    >
                      <Download className="w-5 h-5" />
                      <span className="font-medium">Download Laporan Hasil Survey</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <p className="text-slate-400 italic">Belum ada link LHS tersedia</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="mt-4">
              {canEdit && selectedClaim && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsViewOpen(false);
                    openEditDialog(selectedClaim);
                  }}
                  className="mr-auto"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Klaim
                </Button>
              )}
              <Button variant="outline" onClick={() => setIsViewOpen(false)}>
                Tutup
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus Klaim?</AlertDialogTitle>
              <AlertDialogDescription>
                Apakah Anda yakin ingin menghapus klaim "{selectedClaim?.policy_number}"? 
                Tindakan ini tidak dapat dibatalkan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-claim"
              >
                Hapus
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ClaimsPage;
